import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api-client';
import type { Bootstrap } from './api-types';
import { readPublicAppearance } from './public-appearance';
import { clearSession, loadSession, saveSession, subscribeToSession, type StoredSession } from './session';

interface AuthValue {
  session: StoredSession | null;
  bootstrap: Bootstrap | null;
  isAuthenticated: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (input: { email: string; password: string }) => Promise<void>;
  /** Exchanges the one-time `?code=` from the OAuth callback for a session. */
  completeOAuth: (code: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());

  // The api client refreshes tokens on 401 and writes the new session straight
  // to storage; subscribing keeps React's copy in step with those writes.
  useEffect(() => subscribeToSession(setSession), []);

  const value = useMemo<AuthValue>(
    () => ({
      session,
      bootstrap: session?.bootstrap ?? null,
      isAuthenticated: Boolean(session),
      async signInWithGoogle() {
        // Ask the server to send the one-time code back to this origin's
        // callback route rather than the iOS scheme.
        const callback = `${window.location.origin}/auth/callback`;
        const { authUrl } = await api.googleStart(callback);
        window.location.assign(authUrl);
      },
      async completeOAuth(code: string) {
        saveSession(await api.exchange(code, readPublicAppearance()));
      },
      async signInWithPassword(input) {
        saveSession(await api.password({ ...input, appearance: readPublicAppearance() }));
      },
      signOut() {
        clearSession();
      },
    }),
    [session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside an AuthProvider.');
  return value;
}
